import React, { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DimensionWidgetWrapper,
  SliceIntervalWrapper,
  StyledTabs,
  SlicePositionWrapper,
} from './styled';
import DimensionTables from './DimensionTables';
import { Tabs } from '@store/trends/base/reducer';
import ViewerSessions from './ViewerSessions';
import { enableTableCompare } from '@store//trends/birdChart/actions';
import {
  chartIntervalSelector,
  tableCompareIntervalSelector,
  tableCompareEnabledSelector,
  noBrushSelector,
} from '@store/trends/birdChart/selectors';
import { IntervalLabel } from '@components/dimensions-panel/AnalysisHeader';
import AppUserSessions from '@components/app-viewer-sessions';
import { AppAnalyseNetworkRequest } from '@components/app-analyse-network-request';
import { dimensionPanelActiveTabSelector } from '@store/trends/base/selectors';
import { setDimensionPanelTab, clearMetricFilter } from '@store/trends/base/actions';
import ImpactedViewers from './impactedViewers';
import { dimensionPanelAtom } from '@src/atoms/panels';
import { useAtomValue } from 'jotai';
import ErrorBoundary from '@components/common/Retry/ErrorBoundary';
import {
  DIMENSION_WRAPPER_ID,
  TABLE_COMPARE_COLOR,
  TABLE_COMPARE_OPACITY,
  getCompareIntervalLabel,
  getCompareTooltipText,
} from './utils';
import commonSelector, {
  ovbViewersMetricsEnabledSelector,
  realTimeModeOnTrendsSelector,
  filterColorSelector,
} from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { isEiModule, isAdModule, isAppModule } from '@utils/constants';
import { dodAtom } from '@components/metrics-panel-hz/atoms';
import { Checkbox, Tooltip } from 'antd';
import { track } from '@utils';
import { isValidInterval } from '@src/components/summary-panel/SummaryContainer/utils/dates';
import { get } from 'lodash';
import { activeDashboardSelector } from '@src/store/tempDashboard/selectors';
import { getUseCaseLimitation } from '../common/Dashboard/UseCases/limitation';

function DimensionPanel() {
  const dispatch = useDispatch();

  const tab = useSelector(dimensionPanelActiveTabSelector);
  const setTab = (tab) => {
    dispatch(setDimensionPanelTab(tab));
  };

  const interval = useSelector(chartIntervalSelector);
  const isTableCompareEnabled = useSelector(tableCompareEnabledSelector);
  const compareInterval: any = useSelector(tableCompareIntervalSelector);

  const dodEnabled = useAtomValue(dodAtom);
  const isSliceAvailable = !useSelector(noBrushSelector);

  const showImpactedViewersTab = useSelector(ovbViewersMetricsEnabledSelector);
  const isLiveMode: boolean = useSelector(realTimeModeOnTrendsSelector);
  const { features } = useSelector(commonSelector);

  const compareIntervalLabel = getCompareIntervalLabel({
    isSliceAvailable,
    dodEnabled,
    compareInterval,
  });
  const shouldShowCompareToggle = (isEiModule || isAdModule) && !isLiveMode;
  const filterColor = useSelector(filterColorSelector);
  const isValidCompareInterval = isValidInterval(compareInterval);
  const isCompareToggleEnabled = (isSliceAvailable || dodEnabled) && isValidCompareInterval;
  const tooltipText = getCompareTooltipText({
    isCompareToggleEnabled: isSliceAvailable || dodEnabled,
    isValidCompareInterval,
  });

  const { usecaseKey } = useSelector(activeDashboardSelector);

  const disableNWTab = get(
    getUseCaseLimitation(usecaseKey),
    ['dimensionPanel', 'networkRequestTab', 'disabled'],
    false,
  );

  // show when any of the flag is enabled
  const showViewersTab =
    features[featureNames.VIEWER_DRILLDOWN] ||
    features[featureNames.VIEWER_DRILLDOWN_REST_METRICS] ||
    features[featureNames.VIEWER_DRILLDOWN_DISTRIBUTION];

  const tabList = useMemo(() => {
    const items = [
      {
        key: Tabs.TopTrends,
        label: 'Trends',
        children: <DimensionTables />,
        disabled: false,
      },
    ];

    if (showViewersTab) {
      items.push({
        key: Tabs.ViewerSessions,
        label: 'Viewer Sessions',
        children: <ViewerSessions />,
        disabled: isLiveMode,
      });
    }

    if (showImpactedViewersTab) {
      items.push({
        key: Tabs.TopImpactedViewers,
        label: 'Impacted Viewers',
        children: <ImpactedViewers />,
        disabled: isLiveMode,
      });
    }

    if (isAppModule) {
      items.push({
        key: Tabs.AppUserSessions,
        label: 'Users',
        children: <AppUserSessions />,
        disabled: isLiveMode,
      });
    }

    if (isAppModule) {
      items.push({
        key: Tabs.AppAnalyseNetworkRequest,
        label: 'Network Request',
        children: <AppAnalyseNetworkRequest />,
        disabled: isLiveMode || disableNWTab,
      });
    }

    return items;
  }, [isAppModule, isLiveMode, showViewersTab, showImpactedViewersTab]);

  const onChange = () => {
    const isEnabledFlag = !isTableCompareEnabled;

    if (isEnabledFlag) {
      dispatch(clearMetricFilter());

      const trigger = isSliceAvailable ? 'slice' : 'dod';
      track({
        event: 'TABLE_COMPARE_CLICK',
        data: {
          enabled: isTableCompareEnabled,
          trigger,
        },
      });
    }

    dispatch(enableTableCompare(isEnabledFlag));
  };

  return (
    <DimensionWidgetWrapper id={DIMENSION_WRAPPER_ID}>
      <StyledTabs activeKey={tab} items={tabList} onChange={setTab} />
      <SlicePositionWrapper>
        {shouldShowCompareToggle && (
          <>
            <SliceIntervalWrapper color={TABLE_COMPARE_COLOR}>
              <div
                style={{
                  width: 'fit-content',
                  textAlign: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  paddingLeft: '5px',
                }}
              >
                <Tooltip title={tooltipText}>
                  <Checkbox
                    checked={isTableCompareEnabled}
                    disabled={!isCompareToggleEnabled}
                    onChange={onChange}
                  >
                    {`${compareIntervalLabel} (Beta)`}
                  </Checkbox>
                </Tooltip>
              </div>
            </SliceIntervalWrapper>
            <div style={{ padding: '5px' }}>vs</div>
          </>
        )}
        <SliceIntervalWrapper color={filterColor + TABLE_COMPARE_OPACITY}>
          <div style={{ width: 'fit-content', textAlign: 'center' }}>
            <IntervalLabel interval={interval} />
          </div>
        </SliceIntervalWrapper>
      </SlicePositionWrapper>
    </DimensionWidgetWrapper>
  );
}

const DimensionPanelContainer = () => {
  const showPanel = useAtomValue(dimensionPanelAtom);
  return showPanel ? (
    <ErrorBoundary>
      <DimensionPanel />
    </ErrorBoundary>
  ) : null;
};

export default DimensionPanelContainer;
